import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { A2HSCampaignContext } from './A2HSCampaignContext';
import { A2HSEvent, a2hsEventEmitter, beforeInstallPromptEvent } from '../utils/a2hsEventListeners';
import { BeforeInstallPromptEvent } from '../schema/BeforeInstallPromptEvent';
import { Browser, OperatingSystem } from 'owa-user-agent';
import { deleteA2HSCampaignData } from '../utils/a2hsStorageUtils';
import { isUserCandidateForA2HSCampaign } from '../utils/isUserCandidateForA2HSCampaign';
import {
    A2HSCampaignContextValue,
    A2HSCampaignProviderProps,
} from '../schema/A2HSCampaignProvider.types';

/**
 * Context provider to pass the A2HSCampaign related data and setters.
 * Should be consumed higher up in the component hierarchy.
 * @param props
 */
export const A2HSCampaignProvider: React.FC<A2HSCampaignProviderProps> = props => {
    const [isA2HSCampaignTargetUser, setIsA2HSCampaignTargetUser] = useState(false);
    const [savedBeforeInstallPrompt, setBeforeInstallPrompt] = useState<BeforeInstallPromptEvent>(
        beforeInstallPromptEvent
    );
    const [isA2HSCustomBannerDismissed, setIsA2HSCustomBannerDismissed] = useState(false);

    const endA2HSCampaignForUser = useCallback(() => {
        setIsA2HSCampaignTargetUser(false);
        setBeforeInstallPrompt(null);
        deleteA2HSCampaignData();
    }, []);

    useEffect(() => {
        const saveEvent = (ev: BeforeInstallPromptEvent) => {
            setBeforeInstallPrompt(ev);
        };

        a2hsEventEmitter.on(A2HSEvent.BEFORE_INSTALL_PROMPT, saveEvent);
        return () => {
            a2hsEventEmitter.off(A2HSEvent.BEFORE_INSTALL_PROMPT, saveEvent);
        };
    }, []);

    useEffect(() => {
        a2hsEventEmitter.on(A2HSEvent.APP_INSTALLED, endA2HSCampaignForUser);
        return () => {
            a2hsEventEmitter.off(A2HSEvent.APP_INSTALLED, endA2HSCampaignForUser);
        };
    }, [endA2HSCampaignForUser]);

    useEffect(() => {
        (async () => {
            const isTargetUser = await isUserCandidateForA2HSCampaign(props);
            setIsA2HSCampaignTargetUser(isTargetUser);
        })();
    }, [props.suppressCampaign, props.supportedBrowserConfigs]);

    const contextValue = useMemo<A2HSCampaignContextValue>(
        () => ({
            isA2HSCampaignTargetUser,
            savedBeforeInstallPrompt,
            endA2HSCampaignForUser,
            isA2HSCustomBannerDismissed,
            dismissA2HSCustomBanner: () => setIsA2HSCustomBannerDismissed(true),
        }),
        [
            isA2HSCampaignTargetUser,
            savedBeforeInstallPrompt,
            endA2HSCampaignForUser,
            isA2HSCustomBannerDismissed,
        ]
    );

    return (
        <A2HSCampaignContext.Provider value={contextValue}>
            {props.children}
        </A2HSCampaignContext.Provider>
    );
};

A2HSCampaignProvider.defaultProps = {
    suppressCampaign: false,
    supportedBrowserConfigs: [
        // Android - Chrome: [45, 68), >=76
        /**
         * Caveat:
         * Versions in [68,76) are not being supported because 'beforeInstallPromptEvent.preventDefault()'
         * doesn't let us suppress the browser default mini-infobar banner for A2HS. Associated links:
         *      1. https://developers.google.com/web/updates/2018/06/a2hs-updates
         *      2. https://developers.google.com/web/updates/2019/07/nic76
         */
        {
            browserName: Browser.CHROME,
            browserVersionRange: [[45], [68]],
            operatingSystems: [OperatingSystem.ANDROID],
        },
        {
            browserName: Browser.CHROME,
            browserVersionRange: [76],
            operatingSystems: [OperatingSystem.ANDROID],
        },

        // Android - Edge Anaheim: all versions
        {
            browserName: Browser.EDGE_CHROMIUM,
            operatingSystems: [OperatingSystem.ANDROID],
        },

        // iOS - Safari: >= 13
        {
            browserName: Browser.SAFARI,
            browserVersionRange: [13],
            operatingSystems: [OperatingSystem.IOS],
        },
    ],
};
